import * as Api from '@ViewModels';
import { StyleDeclarationValue, css } from 'aphrodite';
import { observer } from 'mobx-react';
import moment from 'moment';
import * as React from 'react';
import {
	Draggable,
	DraggableProvided,
	DraggableStateSnapshot,
	DraggingStyle,
	NotDraggingStyle,
} from 'react-beautiful-dnd';
import { TemplateCategoryType } from '../../../../extViewmodels';
import { CampaignType } from '../../../../models/AdminModels';
import { useEventLogging } from '../../../../models/Logging';
import { getDefaultDateStringValue } from '../../../../models/UiUtils';
import { useErrorMessages } from '../../../../models/hooks/appStateHooks';
import {
	TemplateCategoriesViewModel,
	TemplateCategoryViewModel,
	TemplateWithStatusesViewModel,
} from '../../../../viewmodels/AdminViewModels';
import {
	Dropdown,
	DropdownContent,
	DropdownEllipsisTrigger,
	DropdownItem,
} from '../../../../web/components-v2/Dropdown';
import { Checkbox } from '../../../../web/components/Checkbox';
import { DragGripperIcon } from '../../../../web/components/svgs/icons/DragGripperIcon';
import { SocialMediaIndicatorIcon } from '../../../../web/components/svgs/icons/SocialMediaIndicatorIcon';
import { baseStyleSheet } from '../../../../web/styles/styles';
import { TemplateActions } from '../TemplateCategory';
import { TemplateCategoryEditDropdownItem } from '../TemplateCategory/components/TemplateCategoryEditMoreMenuItem';
import { TemplateCategoryFeatureDropdownItem } from '../TemplateCategory/components/TemplateCategoryFeatureMoreMenuItem';
import { TemplateCategoryViewDropdownItem } from '../TemplateCategory/components/TemplateCategoryVeiwMoreMenuItem';
import { styleSheet } from './styles';

interface IProps {
	category: TemplateCategoryViewModel;
	className?: string;
	index: number;
	styles?: StyleDeclarationValue[];
	template: TemplateWithStatusesViewModel;
	parentExpanded?: boolean;
	onMenuItemClicked?(menuItem: TemplateActions): void;
	templateType?: CampaignType;
	unpublished?: boolean;
	selectedTemplateIds?: string[];
	toggleSelectedTemplateIds?: (droppableId: string, category: TemplateCategoryViewModel) => void;
	campaignType?: CampaignType;
	categories: TemplateCategoriesViewModel;
}

function _TemplateRow({
	categories,
	category,
	className,
	styles,
	template,
	index,
	parentExpanded,
	onMenuItemClicked,
	unpublished,
	templateType,
	selectedTemplateIds,
	toggleSelectedTemplateIds,
	campaignType,
}: IProps) {
	const { logApiError } = useEventLogging('TemplateRow');
	const errorMessages = useErrorMessages();
	const templateId =
		campaignType === CampaignType.Social && template.templateReference.template?.associatedTemplates?.length > 0
			? template.templateReference.template.associatedTemplates[0].id // add a check that it is an email template type
			: template.templateReference.template.id; // this template could be either email solo, email combo, or social solo
	const expirationDate = template?.templateReference?.template?.schedule?.expirationDate;
	const hasAssociatedCampaign = template?.templateReference?.template?.associatedTemplates?.length > 0;
	const draggableId = template.templateReference.template.id;
	const hasSelectedAnyTemplates = selectedTemplateIds.length > 0;
	const getItemStyle = (isDragging: boolean, draggableStyle: DraggingStyle | NotDraggingStyle): React.CSSProperties => {
		return {
			background: isDragging ? 'white' : '',
			borderBottom: isDragging ? '1px solid #E3E7E9' : '',
			borderRadius: isDragging ? '3px' : '',
			borderTop: isDragging ? '1px solid #E3E7E9' : '',
			...draggableStyle,
		};
	};
	const onTemplateFeatureSuccess = () => {
		const featured = categories.categories?.find(x => x.name === 'Featured');
		featured.setTotal(featured.total + 1);
		featured
			.load(campaignType)

			?.then(() => {
				featured.setExpanded(true);
			})
			?.catch((err: Api.IOperationResultNoValue) => {
				logApiError('LoadTemplateCategory-Error', err);

				errorMessages.pushApiError(err);
			});
	};
	return (
		<Draggable
			disableInteractiveElementBlocking={false}
			draggableId={draggableId}
			isDragDisabled={!parentExpanded}
			index={index}
		>
			{(provided: DraggableProvided, snapshot: DraggableStateSnapshot) => {
				return (
					<div
						className={`${css(
							styleSheet.container,
							...(styles || []),
							...(hasSelectedAnyTemplates ? [styleSheet.containerCompact] : [])
						)} template-row ${className || ''}`}
						ref={provided.innerRef}
						{...(provided?.draggableProps || {})}
						style={getItemStyle(snapshot.isDragging, provided.draggableProps?.style)}
					>
						<Checkbox
							className={css(styleSheet.checkbox)}
							id={`campaignSelector-${draggableId}-checkbox`}
							checked={selectedTemplateIds.indexOf(draggableId) > -1}
							onChange={() => toggleSelectedTemplateIds(draggableId, category)}
						/>

						<div {...(provided?.dragHandleProps || {})} className={css(styleSheet.gripper)}>
							{hasSelectedAnyTemplates ? <span>{index + 1}</span> : <DragGripperIcon />}
						</div>
						<div className={css(styleSheet.items)}>
							<div className={css(styleSheet.name, baseStyleSheet.truncateText)}>
								<div className={css(baseStyleSheet.truncateText)}>{template.refName}</div>
								{unpublished && <span className={css(styleSheet.draft)}>DRAFT</span>}
								{!!expirationDate && (
									<span className={css(styleSheet.expires, moment(expirationDate) < moment() && styleSheet.expired)}>
										{(moment(expirationDate) > moment() ? 'Expires ' : 'Expired ') +
											getDefaultDateStringValue(expirationDate)}
									</span>
								)}
							</div>
							<span className={css(styleSheet.industries)}>{template.refIndustries}</span>
							<span className={css(styleSheet.created)}>{template.refCreated}</span>
							<span className={css(styleSheet.actions, !hasAssociatedCampaign && styleSheet.actionsShift)}>
								<Dropdown>
									<DropdownEllipsisTrigger />
									<DropdownContent>
										{category.type === TemplateCategoryType.Featured ? (
											<>
												<TemplateCategoryEditDropdownItem
													campaignType={campaignType}
													categories={categories}
													template={template}
													categoryName={category.name}
													industry={category.industry}
												/>
												<DropdownItem onClick={() => onMenuItemClicked('unfeature')}>Un-feature</DropdownItem>
											</>
										) : null}
										{category.type === TemplateCategoryType.Archived ? (
											<>
												<DropdownItem onClick={() => onMenuItemClicked('category')}>Restore</DropdownItem>
												<TemplateCategoryViewDropdownItem
													campaignType={campaignType}
													categories={categories}
													template={template}
													industry={category.industry}
												/>
												<DropdownItem onClick={() => onMenuItemClicked('delete')}>Delete</DropdownItem>
											</>
										) : null}
										{category.type === TemplateCategoryType.HtmlNewsletter ? (
											<>
												<TemplateCategoryEditDropdownItem
													campaignType={campaignType}
													categories={categories}
													template={template}
													categoryName={category.name}
													industry={category.industry}
												/>
												<DropdownItem onClick={() => onMenuItemClicked('archive')}>Archive</DropdownItem>
												<DropdownItem onClick={() => onMenuItemClicked('archiveAll')}>Archive All</DropdownItem>
												{campaignType !== CampaignType.Blog ? (
													<DropdownItem onClick={() => onMenuItemClicked('copy')}>Copy Direct Link</DropdownItem>
												) : null}
											</>
										) : null}
										{![
											TemplateCategoryType.Featured,
											TemplateCategoryType.Archived,
											TemplateCategoryType.HtmlNewsletter,
										].includes(category.type) ? (
											<>
												<TemplateCategoryEditDropdownItem
													campaignType={campaignType}
													categories={categories}
													template={template}
													categoryName={category.name}
													industry={category.industry}
												/>
												<DropdownItem onClick={() => onMenuItemClicked('category')}>Change Category</DropdownItem>
												<TemplateCategoryFeatureDropdownItem
													industry={category.industry}
													templateId={templateId}
													campaignType={campaignType}
													onSuccess={onTemplateFeatureSuccess}
												/>
												<DropdownItem onClick={() => onMenuItemClicked('archive')}>Archive This Vertical</DropdownItem>
												<DropdownItem onClick={() => onMenuItemClicked('archiveAll')}>
													Archive All Verticals
												</DropdownItem>
												<DropdownItem onClick={() => onMenuItemClicked('copy')}>Copy Direct Link</DropdownItem>
											</>
										) : null}
									</DropdownContent>
								</Dropdown>
							</span>
						</div>
						{hasAssociatedCampaign && templateType === CampaignType.Email && (
							<div className={css(styleSheet.socialIcon)}>
								<SocialMediaIndicatorIcon />
							</div>
						)}
						{hasAssociatedCampaign && templateType === CampaignType.Social && (
							<div className={css(styleSheet.socialIcon)}>
								<SocialMediaIndicatorIcon fillColor='#89C947' />
							</div>
						)}
					</div>
				);
			}}
		</Draggable>
	);
}

export const TemplateRow = observer(_TemplateRow);
